// Announcement strip under the navigation on the homepage (EN "/" and SK "/sk") inviting to the webinar.
// Hides itself once the webinar starts, or after the visitor closes it (remembered per webinar).
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { WEBINAR } from '@/pages/Webinar';

const DISMISS_KEY = `sd-webinar-bar-closed-${WEBINAR.id}`;

const copy = {
  en: {
    kicker: 'Free webinar',
    note: 'in Slovak',
    title: 'Black Friday that pays off',
    date: 'Tue 10 Nov, 10:00',
    short: 'Free webinar',
    shortDate: '10 Nov, 10:00 · in Slovak',
    cta: 'See the agenda',
    ctaShort: 'Agenda',
    close: 'Close',
    days: (d: number) => (d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d} days`),
  },
  sk: {
    kicker: 'Bezplatný webinár',
    note: '',
    title: WEBINAR.title,
    date: 'utorok 10. 11. o 10:00',
    short: 'Bezplatný webinár',
    shortDate: '10. 11. o 10:00',
    cta: 'Pozrieť program',
    ctaShort: 'Program',
    close: 'Zavrieť',
    days: (d: number) => (d === 0 ? 'dnes' : d === 1 ? 'zajtra' : d < 5 ? `o ${d} dni` : `o ${d} dní`),
  },
};

// Whole calendar days until the webinar day, in the visitor's local time
const daysUntil = () => {
  const start = new Date(WEBINAR.startUtc);
  const today = new Date();
  const a = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / 86400000);
};

const WebinarBar = () => {
  const { language } = useLanguage();
  const c = language === 'sk' ? copy.sk : copy.en;
  const [closed, setClosed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const days = daysUntil();
  const visible = !closed && Date.now() < new Date(WEBINAR.startUtc).getTime();
  const ref = useRef<HTMLDivElement>(null);

  // The strip sits inside the fixed header, so tell the page how much lower to start (Index reads --announce-h)
  useEffect(() => {
    const root = document.documentElement;
    if (!visible || !ref.current) {
      root.style.setProperty('--announce-h', '0px');
      return;
    }
    const el = ref.current;
    const update = () => root.style.setProperty('--announce-h', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--announce-h', '0px');
    };
  }, [visible]);

  if (!visible) return null;

  const close = () => {
    setClosed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode: closes for this page view only */
    }
  };

  return (
    <div ref={ref}>
      <div className="relative overflow-hidden bg-gradient-to-r from-[#FF6B35] via-[#FF7A45] to-[#FF6B35] text-white shadow-[0_6px_20px_-6px_rgba(255,107,53,0.6)]">
        {/* Two light streaks sweep across every few seconds, the second one close behind */}
        <span className="webinar-bar-shine pointer-events-none absolute inset-y-0 -left-1/3 w-1/3" aria-hidden="true" />
        <span className="webinar-bar-shine webinar-bar-shine-2 pointer-events-none absolute inset-y-0 -left-1/3 w-1/6" aria-hidden="true" />
  
        {/* Phones: label with the date under it + button, centred. Desktop: label left, title centre, button right */}
        <div className="relative px-4 md:pl-8 py-3 md:py-3.5 flex items-center justify-center gap-3 md:grid md:grid-cols-[1fr_auto_1fr] md:gap-6 pr-10 md:pr-12">
          <p className="md:hidden min-w-0 leading-tight">
            <span className="block truncate font-bold text-sm min-[360px]:text-[15px]">{c.short}</span>
            <span className="block truncate text-[11px] min-[360px]:text-xs text-white/90">{c.shortDate}</span>
          </p>
          <p className="hidden md:flex items-center gap-2 min-w-0 justify-self-start">
            <span className="webinar-bar-title inline-block ml-1 font-bold uppercase tracking-wide text-sm whitespace-nowrap">{c.kicker}</span>
            {c.note && <span className="text-white/85 text-sm whitespace-nowrap">({c.note})</span>}
          </p>
          <p className="hidden md:flex items-center gap-3 min-w-0 text-base justify-self-center">
            <span className="font-extrabold text-xl truncate">{c.title}</span>
            <span className="text-white/90 whitespace-nowrap">{c.date}</span>
            {days > 0 && (
              <span className="px-2.5 py-0.5 rounded-full bg-[#2A1E47] text-sm font-semibold whitespace-nowrap">{c.days(days)}</span>
            )}
          </p>
          <Link
            to={`/webinar?src=homepage-bar-${language === "sk" ? "sk" : "en"}`}
            className="group shrink-0 md:justify-self-end inline-flex items-center gap-1.5 min-[360px]:gap-2 px-3 min-[360px]:px-4 md:px-5 py-1.5 md:py-2 rounded-lg bg-[#2A1E47] font-bold text-white shadow-md hover:bg-[#3A2A5F] hover:-translate-y-0.5 transition-all"
          >
            {/* Arrow before the text, pointing at it (not towards the close button) */}
            <ArrowRight className="webinar-bar-arrow w-4 h-4" />
            <span className="md:hidden">{c.ctaShort}</span>
            <span className="hidden md:inline">{c.cta}</span>
          </Link>
        </div>
  
        <button
          type="button"
          onClick={close}
          aria-label={c.close}
          className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 p-1.5 rounded text-white/80 hover:text-white hover:bg-white/15 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default WebinarBar;
